import React, {useContext, useEffect, useRef, useState} from 'react';
import {AgentContext} from "./context/agent-context.jsx";
import Swal from "sweetalert2";
import {FaFile, FaHistory, FaPrint} from "react-icons/fa";
import userService from "../../../../services/web/user-service.js";
import {toast} from "react-toastify";
import projectUtils from "../../../../utils/project-utils.js";
import AlertMessages from "../../../../components/messages/alert-message.js";
import UserInitialData from "./utils/user-initial-data.jsx";
import {Template} from "../../../../components/template/template.jsx";
import Tabs from "./components/tabs.jsx";
import Button from "../../../../components/button/button.jsx";
import {Card} from "../../../../components/card/card.jsx";
import UserInactivate from "./components/user-inactivate.jsx";
import CpfModal from "./components/cpf-modal.jsx";
import {validateExistsCpf} from "./components/exists-cpf.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import HeaderTemplateButton from "../../../../components/button/header-template-button.jsx";
import {FaGears} from "react-icons/fa6";

const AgentForm = () => {
    const navigate = navigateHook();
    const {
        id, setIsLoading, user, shared, groupsSelected, rolesSelected, devices, attachs, tablesAttachs, changes,
        userRefetch, userInfo, setUser, isLoading, setChangesContext, tabs, readyToBlock, tabSelected, setTabSelected,
        setPassWordFocus, existsCpf, setExistsCpf, setAttachs, printResume, headerSelected, setHeaderSelected, setPrintModal,
        generalAttachs, setGeneralAttachs, attachesDescription,
    } = useContext(AgentContext);

    const [inactivateModal, setInactivateModal] = useState(false)
    const [readyToActivate, setReadyToActivate] = useState(false)
    const [shouldValidateName, setShouldValidateName] = useState(false);
    const nameInputRef = useRef(null);
    const nameRequiredInvalid = shouldValidateName && !(user.general.name ?? "").trim();

    useEffect(() => {
        if (readyToActivate) save();
    }, [readyToActivate]);

    useEffect(() => {
        if (readyToBlock) save();
    }, [readyToBlock]);

    const focusNameInput = () => {
        const focus = () => nameInputRef.current?.focus();
        const requestFrame = globalThis.requestAnimationFrame;

        if (!requestFrame) {
            globalThis.setTimeout(focus, 0);
            return;
        }

        requestFrame(() => requestFrame(focus));
    };

    const validateRequiredName = () => {
        if ((user.general.name ?? "").trim()) return true;

        setShouldValidateName(true);
        setTabSelected("general");
        Swal.fire({
            icon: "error",
            text: "Preencha os campos obrigatórios",
            confirmButtonText: "OK",
            confirmButtonColor: "#063A6C",
            reverseButtons: true,
            returnFocus: false,
        }).then(focusNameInput);

        return false;
    };

    const save = async (validatedEmailAndPhone = true) => {
        if (!validateRequiredName()) return;

        if (!existsCpf) {
            const ok = await validateExistsCpf(user.general.cpf, id);
            if (!ok) return;
            setExistsCpf(true);
        }

        const validated = validatedData(validatedEmailAndPhone);
        if (!validated) return;
        const target = id ? "update" : "create";

        const formData = preparedData();

        setIsLoading(true);

        userService.save(target, formData)
            .then((data) => {
                projectUtils.success(!id);

                setAttachs([]);
                setGeneralAttachs([]);

                if (id) {
                    userRefetch();
                    return;
                }

                const redir = tabs.find(tab => tab.key === 'user') ? "user-agents" : "personal-agents"
                const urlDev = `/${redir}/form/${data.id}`

                navigate(urlDev);

            })
            .catch((error) => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = AlertMessages[message] ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => {
                setIsLoading(false)
                if(readyToActivate){
                    Swal.fire({
                        title: "Atenção",
                        text: "Cadastre uma senha para que o agente tenha acesso ao sistema.",
                        icon: "warning",
                        confirmButtonText: "Continuar",
                        confirmButtonColor: "#063A6C",
                        cancelButtonText: "Cancelar",
                        showCancelButton: true,
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    }).then(({isConfirmed}) => {
                        if (isConfirmed) {
                            setTabSelected("user")
                            setPassWordFocus(true)
                        }
                    });
                }else{
                    setPassWordFocus(false)
                }
                setReadyToActivate(false);
            });
    }

    const preparedData = () => {
        const formData = new FormData();

        const preparedData = {
            ...user,
            general: {
                ...user.general,
                cpf: user.general.cpf ? user.general.cpf.replace(/\D/g, "") : null,
                shareDeviceId: shared.sharedDevice?.value || null,
                shareEmail: shared.shareEmail,
                shareContactInfo: shared.shareContactInfo,
                focalPoints: shared.focalPoints.map(fp => fp.value),
                address: {
                    ...user.general.address,
                    zipcode: user.general.address.zipcode?.replace(/\D/g, "") || null
                }
            },
            user: {
                ...user.user,
                login: user.user.login ? user.user.login.toUpperCase() : null,
                groups: groupsSelected.map(g => g.value),
                roles: rolesSelected.map(r => r.value)
            },
            devices
        }

        if (id) {
            preparedData.id = id;
        } else {
            preparedData.user.changePassword = true
        }

        delete preparedData.vmsDate

        if (!id && !user.general.cpf) preparedData.general.noCpfJustification = user.general.noCpfJustification

        if (user.general.image) formData.append('image', user.general.image);

        if (user.user.password) preparedData.user.password = user.user.password;

        const attachDescription = Object.entries(attachesDescription)
            .map(([attachId, description]) => {
               const attach = [
                   ...(user.attachs ?? []),
                   ...generalAttachs
               ].find(attach => attach.attachId === attachId);

               return {
                   fileName: attach?.name,
                   description,
                   attachId
               }
            });

        formData.append('data', JSON.stringify(preparedData));

        formData.append('attachDescription', JSON.stringify(attachDescription));

        for (const attach of attachs) {
            formData.append("attachs", attach)
        }

        for (const attach of tablesAttachs) {
            formData.append("attachs", attach)
        }

        for (const attach of generalAttachs) {
            formData.append("generalAttachs", attach);
        }

        return formData
    }

    const validatedData = (validateEmailAndPhone) => {
        if (user.user.password.trim() !== user.user.passwordConfirm.trim()) {
            projectUtils.swalErrorMessage("", AlertMessages["password.confirm"], "Ok");
            return false;
        }

        let NoEmailOrPrincipalContact = !user.general.email
        if (id) NoEmailOrPrincipalContact = !user.general.email && !devices.some(c => c.isMain);

        if (NoEmailOrPrincipalContact && validateEmailAndPhone && user.user.login) {
            Swal.fire({
                title: "Atenção",
                html: 'O agente não possui informações para recuperação de senha! Para confirmar, digite <b>não recuperar senha</b> abaixo:',
                icon: "warning",
                input: "text",
                confirmButtonText: "Confirmar",
                confirmButtonColor: "#063A6C",
                cancelButtonText: "Cancelar",
                showCancelButton: true,
                reverseButtons: true,
                preConfirm(inputValue) {
                    if (inputValue.trim() !== "não recuperar senha") {
                        Swal.showValidationMessage("Digite corretamente a palavra-chave");
                    }
                }
            }).then(({isConfirmed}) => {
                if (isConfirmed) save(false);
            });

            return false;
        }

        return true;
    }

    const cancel = () => {
        if (id) {
            userRefetch();
        } else {
            setUser(UserInitialData)
        }

        setChangesContext([])
        setShouldValidateName(false);
    }

    const getTab = () => {
        const tabFind = tabs.find(tab => tab.key === tabSelected);
        if (!tabFind) return "";

        return tabFind.key === "general"
            ? React.cloneElement(tabFind.component, {nameRequiredInvalid, nameInputRef})
            : tabFind.component;
    }

    const activate = () => {
        setUser(prev => ({...prev, user: {...user.user, status: true, changePassword: true}}))
        setReadyToActivate(true);
    }

    const reportAudit = () => {
        const url = `/reports/audit?userHistory=${id}`
        navigate(url, true);
    }

    return (<>

        <Template isLoading={isLoading}>
            <Template.Header>
                <div className={"w-full flex justify-between"}>
                    <Template.Title>
                        Agente
                    </Template.Title>

                    { id &&
                        <div className={"flex gap-2"}>
                            { tabSelected !== "resume" && tabs.find(tab => tab.key === 'user') && (
                                <Button onClick={reportAudit}>
                                    <FaHistory/> Histórico
                                </Button>
                            )}

                            { tabSelected === "resume" && tabs.find(tab => tab.key === "resume") && (
                                <>
                                    <Button title={'Opções de impressão'} onClick={() => setPrintModal(true)}>
                                        <FaGears size={16}/>
                                    </Button>

                                    <Button title={'Imprimir'} onClick={printResume}>
                                        <FaPrint/>
                                    </Button>

                                    <HeaderTemplateButton
                                        selected={headerSelected}
                                        setSelected={setHeaderSelected}
                                    />
                                </>
                            )}
                        </div>
                    }
                </div>
            </Template.Header>

            <Template.Body>
                <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2 flex flex-col h-[calc(100vh-69px)]">
                        <Tabs tabSelected={tabSelected} setTabSelected={setTabSelected} hasGeneralRequiredError={nameRequiredInvalid}/>
                    </div>
                    <div className="col-span-10 relative">
                        <div className={"user-report"}>
                            {getTab()}
                        </div>

                        <Card className={"mt-2"}>
                            <Card.Body>
                                <div className={"flex justify-end gap-2"}>
                                    {
                                        (id && user.user.status) &&
                                        <Button target={"danger"} onClick={() => setInactivateModal(true)}>
                                            Inativar
                                        </Button>
                                    }

                                    {
                                        (id && !user.user.status) &&
                                        <>
                                            <Button onClick={activate}>
                                                Ativar
                                            </Button>
                                        </>
                                    }

                                    <Button onClick={save} disabled={changes.length === 0} actionFeedbackType='save' onActionFeedbackSuccess={() => setChangesContext([])}>

                                        Salvar
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </Template.Body>
        </Template>


        <UserInactivate
            open={inactivateModal}
            setOpen={setInactivateModal}
            save={save}
        />

        <CpfModal
            id={id}
            noCpfJustificationRequiredLength={userInfo?.noCpfJustificationRequiredLength}
        />
    </>);
};

export default AgentForm;
